import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Row,
  Col,
  Button,
  Table,
  Modal,
  Form,
} from "react-bootstrap";

import { REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL } from "config";

const baseUrl = REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL;

const emptyCategory = {
  slug: "",
  name: "",
  sortOrder: 0,
  active: true,
};

const emptySpecField = {
  key: "",
  label: "",
  valueKind: "single",
  sortOrder: 0,
};

const emptyOption = (fieldId) => ({
  fieldId: fieldId || "",
  slug: "",
  label: "",
  sortOrder: 0,
  active: true,
});

const BaseProductMetadata = () => {
  const [categories, setCategories] = useState([]);
  const [specFields, setSpecFields] = useState([]);
  const [loadError, setLoadError] = useState("");

  const [catModal, setCatModal] = useState({ show: false, editing: null });
  const [catForm, setCatForm] = useState(emptyCategory);

  const [fieldModal, setFieldModal] = useState({ show: false, editing: null });
  const [fieldForm, setFieldForm] = useState(emptySpecField);

  const [optFieldId, setOptFieldId] = useState("");
  const [optModal, setOptModal] = useState({ show: false, editing: null });
  const [optForm, setOptForm] = useState(emptyOption(""));

  const loadCategories = useCallback(async () => {
    const { data } = await axios.get(
      `${baseUrl}/api/admin/categories?includeInactive=1`
    );
    setCategories(data.categories || []);
  }, []);

  const loadSpecFields = useCallback(async () => {
    const { data } = await axios.get(
      `${baseUrl}/api/admin/spec-fields?includeOptions=1`
    );
    setSpecFields(data.specFields || []);
  }, []);

  const loadAll = useCallback(async () => {
    setLoadError("");
    try {
      await Promise.all([loadCategories(), loadSpecFields()]);
    } catch (e) {
      setLoadError("Could not load. Sign in as admin and try again.");
      console.log(e);
    }
  }, [loadCategories, loadSpecFields]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const openCategory = (c) => {
    if (c) {
      setCatForm({
        slug: c.slug,
        name: c.name,
        sortOrder: c.sortOrder,
        active: c.active !== false,
      });
      setCatModal({ show: true, editing: c });
    } else {
      setCatForm(emptyCategory);
      setCatModal({ show: true, editing: null });
    }
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    try {
      if (catModal.editing) {
        await axios.put(
          `${baseUrl}/api/admin/categories/${catModal.editing._id}`,
          catForm
        );
      } else {
        await axios.post(`${baseUrl}/api/admin/categories`, catForm);
      }
      setCatModal({ show: false, editing: null });
      await loadCategories();
    } catch (err) {
      alert(err.response?.data?.msg || err.message);
    }
  };

  const deleteCategory = async (c) => {
    if (!window.confirm(`Delete category “${c.name}”?`)) {
      return;
    }
    try {
      await axios.delete(`${baseUrl}/api/admin/categories/${c._id}`);
      await loadCategories();
    } catch (err) {
      alert(
        err.response?.data?.msg || err.message || "Delete failed. Try setting inactive in Edit."
      );
    }
  };

  const openField = (f) => {
    if (f) {
      setFieldForm({
        key: f.key,
        label: f.label,
        valueKind: f.valueKind,
        sortOrder: f.sortOrder,
      });
      setFieldModal({ show: true, editing: f });
    } else {
      setFieldForm(emptySpecField);
      setFieldModal({ show: true, editing: null });
    }
  };

  const saveField = async (e) => {
    e.preventDefault();
    try {
      if (fieldModal.editing) {
        await axios.put(
          `${baseUrl}/api/admin/spec-fields/${fieldModal.editing._id}`,
          fieldForm
        );
      } else {
        await axios.post(`${baseUrl}/api/admin/spec-fields`, fieldForm);
      }
      setFieldModal({ show: false, editing: null });
      await loadSpecFields();
    } catch (err) {
      alert(err.response?.data?.msg || err.message);
    }
  };

  const deleteField = async (f) => {
    if (
      !window.confirm(
        `Delete field “${f.label}” and all of its options? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${baseUrl}/api/admin/spec-fields/${f._id}`);
      if (optFieldId === f._id) {
        setOptFieldId("");
      }
      await loadSpecFields();
    } catch (err) {
      alert(err.response?.data?.msg || err.message);
    }
  };

  const optionableFields = specFields.filter(
    (f) => f.valueKind === "single" || f.valueKind === "multi"
  );
  const selectedFieldForOptions = specFields.find(
    (f) => String(f._id) === String(optFieldId)
  );
  const optionsForSelected = (selectedFieldForOptions &&
    selectedFieldForOptions.options) || [];

  const openOption = (o) => {
    if (o) {
      setOptForm({
        fieldId: optFieldId,
        slug: o.slug,
        label: o.label,
        sortOrder: o.sortOrder,
        active: o.active !== false,
      });
      setOptModal({ show: true, editing: o });
    } else {
      if (!optFieldId) {
        return alert("Select a spec field first.");
      }
      setOptForm(emptyOption(optFieldId));
      setOptModal({ show: true, editing: null });
    }
  };

  const saveOption = async (e) => {
    e.preventDefault();
    try {
      if (optModal.editing) {
        await axios.put(
          `${baseUrl}/api/admin/spec-field-options/${optModal.editing._id}`,
          {
            slug: optForm.slug,
            label: optForm.label,
            sortOrder: optForm.sortOrder,
            active: optForm.active,
          }
        );
      } else {
        await axios.post(`${baseUrl}/api/admin/spec-field-options`, {
          fieldId: optForm.fieldId,
          slug: optForm.slug,
          label: optForm.label,
          sortOrder: optForm.sortOrder,
          active: optForm.active,
        });
      }
      setOptModal({ show: false, editing: null });
      await loadSpecFields();
    } catch (err) {
      alert(err.response?.data?.msg || err.message);
    }
  };

  const deleteOption = async (o) => {
    if (!window.confirm(`Delete option “${o.label}”?`)) {
      return;
    }
    try {
      await axios.delete(
        `${baseUrl}/api/admin/spec-field-options/${o._id}`
      );
      await loadSpecFields();
    } catch (err) {
      alert(err.response?.data?.msg || err.message);
    }
  };

  return (
    <div style={{ padding: 40, maxWidth: 1100 }}>
      <Row className="mb-4">
        <Col>
          <h1>Base product metadata</h1>
          <p className="text-muted">
            Manage marketing categories, spec field definitions, and option
            lists. Then assign them on{" "}
            <strong>Admin → Products</strong> when editing a product that is
            registered as a base product; add base products in Product
            compatibilities if needed.
          </p>
          {loadError && <p className="text-danger">{loadError}</p>}
        </Col>
        <Col xs="auto">
          <Button onClick={loadAll}>Refresh</Button>
        </Col>
      </Row>

      <h4 className="mt-2">Categories</h4>
      <Button
        className="mb-2"
        onClick={() => {
          openCategory(null);
        }}
      >
        Add category
      </Button>
      <Table striped bordered size="sm">
        <thead>
          <tr>
            <th>Sort</th>
            <th>Name</th>
            <th>Slug</th>
            <th>Active</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {categories.map((c) => (
            <tr key={c._id}>
              <td>{c.sortOrder}</td>
              <td>{c.name}</td>
              <td>{c.slug}</td>
              <td>{c.active ? "Yes" : "No"}</td>
              <td>
                <Button
                  size="sm"
                  className="me-1"
                  onClick={() => {
                    openCategory(c);
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => {
                    deleteCategory(c);
                  }}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <h4 className="mt-4">Spec fields</h4>
      <p className="text-muted" style={{ fontSize: 14 }}>
        <strong>Kind:</strong> single = one option, multi = many, number, text
        (no option list). Delete removes all options for that field.
      </p>
      <Button
        className="mb-2"
        onClick={() => {
          openField(null);
        }}
      >
        Add spec field
      </Button>
      <Table striped bordered size="sm">
        <thead>
          <tr>
            <th>Sort</th>
            <th>Key</th>
            <th>Label</th>
            <th>Kind</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {specFields.map((f) => (
            <tr key={f._id}>
              <td>{f.sortOrder}</td>
              <td>
                <code>{f.key}</code>
              </td>
              <td>{f.label}</td>
              <td>{f.valueKind}</td>
              <td>
                <Button
                  size="sm"
                  className="me-1"
                  onClick={() => {
                    openField(f);
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => {
                    deleteField(f);
                  }}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <h4 className="mt-4">Options (for single / multi fields)</h4>
      <Form.Group className="mb-2" style={{ maxWidth: 400 }}>
        <Form.Label>Field</Form.Label>
        <Form.Control
          as="select"
          value={optFieldId}
          onChange={(e) => {
            setOptFieldId(e.target.value);
          }}
        >
          <option value="">— Select —</option>
          {optionableFields.map((f) => (
            <option key={String(f._id)} value={String(f._id)}>
              {f.label} ({f.key})
            </option>
          ))}
        </Form.Control>
      </Form.Group>
      {optFieldId && (
        <>
          <Button
            className="mb-2 me-1"
            onClick={() => {
              openOption(null);
            }}
          >
            Add option
          </Button>
          <Table striped bordered size="sm">
            <thead>
              <tr>
                <th>Sort</th>
                <th>Label</th>
                <th>Slug</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {optionsForSelected.map((o) => (
                <tr key={o._id}>
                  <td>{o.sortOrder}</td>
                  <td>{o.label}</td>
                  <td>
                    <code>{o.slug}</code>
                  </td>
                  <td>{o.active ? "Yes" : "No"}</td>
                  <td>
                    <Button
                      size="sm"
                      className="me-1"
                      onClick={() => {
                        openOption(o);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => {
                        deleteOption(o);
                      }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      <Modal
        show={catModal.show}
        onHide={() => {
          setCatModal({ show: false, editing: null });
        }}
      >
        <Form onSubmit={saveCategory}>
          <Modal.Header closeButton>
            <Modal.Title>
              {catModal.editing ? "Edit category" : "Add category"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>Slug</Form.Label>
              <Form.Control
                value={catForm.slug}
                onChange={(e) => {
                  setCatForm((s) => ({ ...s, slug: e.target.value }));
                }}
                required
                disabled={!!catModal.editing}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={catForm.name}
                onChange={(e) => {
                  setCatForm((s) => ({ ...s, name: e.target.value }));
                }}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Sort order</Form.Label>
              <Form.Control
                type="number"
                value={catForm.sortOrder}
                onChange={(e) => {
                  setCatForm((s) => ({
                    ...s,
                    sortOrder: parseInt(e.target.value, 10) || 0,
                  }));
                }}
              />
            </Form.Group>
            <Form.Check
              type="checkbox"
              label="Active"
              checked={catForm.active}
              onChange={(e) => {
                setCatForm((s) => ({ ...s, active: e.target.checked }));
              }}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button type="submit" variant="primary">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={fieldModal.show}
        onHide={() => {
          setFieldModal({ show: false, editing: null });
        }}
      >
        <Form onSubmit={saveField}>
          <Modal.Header closeButton>
            <Modal.Title>
              {fieldModal.editing ? "Edit spec field" : "Add spec field"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>Key (stable id)</Form.Label>
              <Form.Control
                value={fieldForm.key}
                onChange={(e) => {
                  setFieldForm((s) => ({ ...s, key: e.target.value }));
                }}
                required
                disabled={!!fieldModal.editing}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Label</Form.Label>
              <Form.Control
                value={fieldForm.label}
                onChange={(e) => {
                  setFieldForm((s) => ({ ...s, label: e.target.value }));
                }}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Kind</Form.Label>
              <Form.Control
                as="select"
                value={fieldForm.valueKind}
                onChange={(e) => {
                  setFieldForm((s) => ({ ...s, valueKind: e.target.value }));
                }}
              >
                <option value="single">single</option>
                <option value="multi">multi</option>
                <option value="number">number</option>
                <option value="text">text</option>
              </Form.Control>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Sort order</Form.Label>
              <Form.Control
                type="number"
                value={fieldForm.sortOrder}
                onChange={(e) => {
                  setFieldForm((s) => ({
                    ...s,
                    sortOrder: parseInt(e.target.value, 10) || 0,
                  }));
                }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button type="submit" variant="primary">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={optModal.show}
        onHide={() => {
          setOptModal({ show: false, editing: null });
        }}
      >
        <Form onSubmit={saveOption}>
          <Modal.Header closeButton>
            <Modal.Title>
              {optModal.editing ? "Edit option" : "Add option"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>Slug</Form.Label>
              <Form.Control
                value={optForm.slug}
                onChange={(e) => {
                  setOptForm((s) => ({ ...s, slug: e.target.value }));
                }}
                required
                disabled={!!optModal.editing}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Label</Form.Label>
              <Form.Control
                value={optForm.label}
                onChange={(e) => {
                  setOptForm((s) => ({ ...s, label: e.target.value }));
                }}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Sort order</Form.Label>
              <Form.Control
                type="number"
                value={optForm.sortOrder}
                onChange={(e) => {
                  setOptForm((s) => ({
                    ...s,
                    sortOrder: parseInt(e.target.value, 10) || 0,
                  }));
                }}
              />
            </Form.Group>
            <Form.Check
              type="checkbox"
              label="Active"
              checked={optForm.active}
              onChange={(e) => {
                setOptForm((s) => ({ ...s, active: e.target.checked }));
              }}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button type="submit" variant="primary">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default BaseProductMetadata;
